import {
    bexHttpClient
} from '../../clients';
import {
    KnowledgeBuilderProxyRequest,
    KnowledgeBuilderProxyAPIResponse
} from '../bexService.types';

/**
 * Knowledge Builder service — DIRECT calls through Envoy.
 *
 * Formerly wrapped every call in the bex-server `/knowledge-builder/proxy`
 * envelope ({endPoint, method, body}); now the endpoint is called directly:
 * `/api/knowledge/<path>` → Envoy (session JWT verified at the edge) → KB.
 * Envoy rewrites `/api/knowledge/` to KB's `/v2/kb/`, so callers keep passing
 * the same KB paths as before (e.g. "v2/kb/api/v1/search/") — the `v2/kb/`
 * prefix is stripped here.
 */
class KnowledgeBuilderService {
    /**
     * Sends a request directly to a KB endpoint (via Envoy).
     * @param {string} url - The KB endpoint, e.g. "v2/kb/api/v1/search/".
     * @param {Object} options - The request options.
     * @param {string} options.method - The request method
     * @param {Object} [options.data] - The data to be sent.
     * @param {string} [options.timeout] - The request timeout in milliseconds.
     * @returns {Promise<Object>} The response body from KB.
     */
    async performRequest(url: string, options: { method?: KnowledgeBuilderProxyRequest['method']; data?: KnowledgeBuilderProxyRequest['body']; timeout?: number } = {}): Promise<KnowledgeBuilderProxyAPIResponse['data']> {
        const response = await this.performRequestWithFullResponse(url, options);
        return response.data;
    }

    /**
     * Same as performRequest but returns the full axios response.
     */
    async performRequestWithFullResponse(url: string, options: { method?: KnowledgeBuilderProxyRequest['method']; data?: KnowledgeBuilderProxyRequest['body']; timeout?: number; headers?: Record<string, string> } = {}): Promise<KnowledgeBuilderProxyAPIResponse> {
        const {
            method = 'GET', data, timeout = 120000, headers
        } = options;
        const path = url.replace(/^\/+/, '').replace(/^v2\/kb\//, '');
        const response = await bexHttpClient.request({
            url: `/knowledge/${path}`,
            method,
            data,
            headers,
            timeout,
        });
        return response as KnowledgeBuilderProxyAPIResponse;
    }
}

const knowledgeBuilderService = new KnowledgeBuilderService();
export { knowledgeBuilderService };
